import React, { useEffect, useMemo, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import InputComponents from '../components/input';
import logo21 from '../images/logo_clock.png';
import { getBilling, getCouponSearch, getPlan, purchasePlan } from '../logic/plans/action';
import { changePlanId, changePlanPriceId, resetAddCoupon, successCouponsSingle } from '../logic/plans/reducer';
import { errorMessage, successMessage } from '../logic/error/action';
import { postData } from '../utils/serverHelper';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

const PricePlan = () => {
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.plans);
  const selectorAuth = useSelector((state) => state.auth);

  const [openSummary, setOpenSummary] = useState(false);
  const [clickedCoupon, setClickedCoupon] = useState(false);
  const [coupon, setCoupon] = useState('');

  useEffect(() => {
    getBilling(dispatch);
    getPlan(dispatch);
  }, [dispatch]);

  const paidPlans = useMemo(
    () =>
      (selector.plans || []).filter(
        (p) => !p.name?.toLowerCase().includes('free') && Number(p.price) > 0
      ),
    [selector.plans]
  );

  const freePlan = useMemo(
    () =>
      (selector.plans || []).find(
        (p) => p.name?.toLowerCase().includes('free') || Number(p.price) === 0
      ),
    [selector.plans]
  );

  const pricesForPlan = (planId) =>
    (selector.plansPrices || []).filter((p) => p.subscription === planId);

  const selectedPlan = selector.plans?.find((p) => p.id === selector.selectedPlan);
  const selectedPrice = selector.plansPrices?.find((p) => p.id === selector.selectedPlanPrice);

  const finalDiscount = useMemo(() => {
    if (!selectedPrice) return 0;
    const couponRow = Array.isArray(selector.couponSingle) ? selector.couponSingle[0] : null;
    if (!couponRow) return 0;
    if (couponRow.ispercent === 1) {
      return (selectedPrice.price * couponRow.price) / 100;
    }
    return couponRow.price;
  }, [selectedPrice, selector.couponSingle]);

  const finalPrice = useMemo(() => {
    if (!selectedPrice) return 0;
    let amount = selectedPrice.price - finalDiscount - (selectorAuth.user?.credit || 0);
    return amount > 0 ? amount : 0;
  }, [selectedPrice, finalDiscount, selectorAuth.user?.credit]);

  const totalPrice = finalPrice + finalPrice * 0.18;

  const selectPlan = (planId) => {
    dispatch(changePlanId(planId));
    const prices = pricesForPlan(planId);
    if (prices.length > 0) {
      dispatch(changePlanPriceId(prices[0].id));
    }
  };

  const proceedWithCredit = async () => {
    try {
      await postData(`/subscription/${selector.selectedPlan}`, {
        month: selector.selectedPlanPrice,
        coupon: 0,
      });
      await getBilling(dispatch);
      dispatch(successCouponsSingle([]));
      setOpenSummary(false);
      successMessage(dispatch, 'Your plan is purchased successfully');
      window.location.href = '/admin/';
    } catch (error) {
      errorMessage(dispatch, error?.response?.data?.message || 'Something went wrong');
    }
  };

  const openCheckout = () => {
    if (!selector.selectedPlan || !selector.selectedPlanPrice) {
      errorMessage(dispatch, 'Please select a plan and billing period');
      return;
    }
    dispatch(resetAddCoupon());
    setOpenSummary(true);
  };

  const renderFeatures = (plan) => {
    const raw = plan.features || '';
    const items = raw.includes('-')
      ? raw.split('-').filter(Boolean)
      : raw.split('\n').filter(Boolean);
    return items.map((feature, i) => (
      <li key={i} className="ml-4 list-disc text-sm text-gray-700">
        {feature.trim()}
      </li>
    ));
  };

  return (
    <AppLayout>
      <div className="w-full min-h-screen bg-gradient-to-b from-blue-50 to-white pb-12">
        <header className="flex justify-start items-center gap-4 flex-col sm:flex-row p-4">
          <img src={logo21} alt="logo" className="w-16 ml-4" />
          <h1 className="text-blue-800 font-bold text-2xl">Choose Your Plan</h1>
          <Box sx={{ ml: 'auto', mr: 2 }}>
            <Button component={Link} to="/admin/subscription/billing" sx={{ textTransform: 'none' }}>
              Billing history
            </Button>
            <Button component={Link} to="/admin/" sx={{ textTransform: 'none' }}>
              Dashboard
            </Button>
          </Box>
        </header>

        <div className="w-full max-w-6xl mx-auto px-4 flex flex-wrap justify-center gap-6">
          {paidPlans.map((plan) => {
            const prices = pricesForPlan(plan.id);
            const defaultPrice = prices[0];
            const isSelected = selector.selectedPlan === plan.id;

            return (
              <div
                key={plan.id}
                className={`w-full sm:w-72 flex flex-col gap-4 rounded-2xl p-6 shadow-lg bg-white border-2 transition-all cursor-pointer ${
                  isSelected ? 'border-blue-600 scale-105' : 'border-transparent'
                }`}
                onClick={() => selectPlan(plan.id)}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                  {plan.name}
                </Typography>
                {defaultPrice && (
                  <Typography variant="h6" color="primary">
                    ₹{defaultPrice.price} + 18% GST
                    <Typography component="span" variant="caption" display="block">
                      {defaultPrice.month} month{defaultPrice.month > 1 ? 's' : ''}
                    </Typography>
                  </Typography>
                )}
                <ul className="flex-1 flex flex-col gap-1">{renderFeatures(plan)}</ul>
                {plan.sms_limit > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    SMS reminders: {plan.sms_limit}
                  </Typography>
                )}
                <Button
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectPlan(plan.id);
                    openCheckout();
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  {isSelected ? 'Selected' : 'Select'}
                </Button>
              </div>
            );
          })}
        </div>

        {paidPlans.length > 0 && (
          <Box sx={{ maxWidth: 400, mx: 'auto', mt: 6, px: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Billing period</InputLabel>
              <Select
                label="Billing period"
                value={selector.selectedPlanPrice || ''}
                onChange={(e) => dispatch(changePlanPriceId(parseInt(e.target.value, 10)))}
              >
                {pricesForPlan(selector.selectedPlan).map((ep) => (
                  <MenuItem key={ep.id} value={ep.id}>
                    {ep.month} months — ₹{ep.price}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 2, textTransform: 'none', py: 1.5 }}
              onClick={openCheckout}
            >
              Proceed to checkout
            </Button>
          </Box>
        )}

        {freePlan && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              component={Link}
              to="/admin/"
              sx={{ textTransform: 'none', fontWeight: 600 }}
              onClick={() => {
                dispatch(changePlanId(freePlan.id));
                const prices = pricesForPlan(freePlan.id);
                if (prices[0]) dispatch(changePlanPriceId(prices[0].id));
              }}
            >
              Continue with free trial
            </Button>
          </Box>
        )}
      </div>

      {openSummary && (
        <div className="w-screen h-screen fixed top-0 left-0 bg-black/60 z-50 flex items-center justify-center p-4">
          {finalPrice <= 0 ? (
            <div className="w-full max-w-md bg-white rounded-lg p-8 flex flex-col gap-4 relative">
              <MdClose
                className="absolute top-4 right-4 cursor-pointer"
                onClick={() => setOpenSummary(false)}
              />
              <Typography variant="h6" fontWeight={700}>
                Use account credits
              </Typography>
              <Typography>
                You have ₹{(selectorAuth?.user?.credit || 0).toFixed(2)} credits available.
              </Typography>
              <Button variant="contained" onClick={proceedWithCredit} sx={{ textTransform: 'none' }}>
                Use credits and continue
              </Button>
            </div>
          ) : (
            <div className="w-full max-w-lg bg-white rounded-lg p-8 flex flex-col gap-4 relative max-h-[90vh] overflow-y-auto">
              <MdClose
                className="absolute top-4 right-4 cursor-pointer"
                onClick={() => setOpenSummary(false)}
              />
              <Typography variant="h5" fontWeight={700}>
                Order summary
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <InputComponents
                  label="Coupon code"
                  placeholder="Enter coupon"
                  onChange={(e) => setCoupon(e.target.value)}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setClickedCoupon(true);
                    getCouponSearch(dispatch, coupon);
                  }}
                  sx={{ mt: 1, textTransform: 'none' }}
                >
                  Apply
                </Button>
              </Box>
              {clickedCoupon && Array.isArray(selector.couponSingle) && (
                <Typography
                  variant="body2"
                  color={selector.couponSingle.length > 0 ? 'success.main' : 'error.main'}
                >
                  {selector.couponSingle.length > 0 ? 'Coupon applied' : 'Invalid coupon'}
                </Typography>
              )}

              <table className="w-full text-sm">
                <tbody>
                  <tr>
                    <td className="py-1">
                      {selectedPlan?.name} — {selectedPrice?.month} months
                    </td>
                    <td className="text-right">₹{selectedPrice?.price || 0}</td>
                  </tr>
                  <tr>
                    <td>Credit</td>
                    <td className="text-right">- ₹{(selectorAuth?.user?.credit || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>Discount</td>
                    <td className="text-right">- ₹{finalDiscount.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td>GST (18%)</td>
                    <td className="text-right">+ ₹{(finalPrice * 0.18).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pt-2">Total</td>
                    <td className="text-right font-bold pt-2">₹{totalPrice.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>

              <Button
                variant="contained"
                fullWidth
                sx={{ textTransform: 'none' }}
                onClick={() =>
                  purchasePlan(
                    dispatch,
                    selector.selectedPlan,
                    selector.selectedPlanPrice,
                    Array.isArray(selector.couponSingle) && selector.couponSingle.length > 0
                      ? selector.couponSingle[0].id
                      : 0
                  )
                }
              >
                Pay now
              </Button>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
};

export default PricePlan;
